<script setup lang="ts">
/**
 * AgentLayout v2 — кабинет агента (DEV-490 десктоп, DEV-498 мобайл; Figma QLdiQ1).
 * Десктоп (>900px): сайдбар + main (топбар с заголовком/поиском + контент).
 * Мобайл (≤900px): верхний бар (☰ + заголовок + аватар) + контент + нижний таб-бар
 * (Дашборд/Клиенты/Сделки/Выплаты/Ещё). ☰ и «Ещё» открывают drawer-меню
 * (юзер-карточка + Разделы + Настройки). Светлая палитра пиннится для всего поддерева.
 */
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { BellIcon } from 'lucide-vue-next'
import { blockedRedirect } from '@/api/interceptors/access.interceptor'
import { AppButton, AppSelect } from '@/design-system'
import type { SectionKey } from '@/features/auth/sections/registry'
import { useAuthStore } from '@/features/auth/stores/auth.store'
import { useSectionsStore } from '@/features/auth/stores/sections.store'
import { agentsApi } from '@/features/agent/api/agents.api'
import AgentAuthPanel from '@/features/agent/components/AgentAuthPanel.vue'
import NotificationPanel from '@/features/notifications/components/NotificationPanel.vue'
import { useNotifications } from '@/features/notifications/composables/useNotifications'
import logoBlue from '@/assets/vinprop-agents-logo.svg'
import { setStoredLocale } from '@/i18n'
import { availableLocales, type Locale } from '@/i18n/locales'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
// Вкладка просмотра «глазами агента» (DEV-2400): «Выход» в ней завершает просмотр.
const impersonating = authStore.impersonation !== null
const sectionsStore = useSectionsStore()
const { t, locale } = useI18n()

const isAuthenticated = computed(() => authStore.isAuthenticated)

// Профиль для юзер-карточки/приветствия (только в авторизованном кабинете).
const profileName = ref('')
const profileAgency = ref('')
const profileReward = ref<number | null>(null)

// Уведомления кабинета (DEV-1017): колокольчик + live-стрим по SSE.
const { unreadCount, togglePanel, connectStream, disconnectStream } = useNotifications()

// Блокировка агента (DEV-2087). Статус приходит в том же профиле — отдельного
// запроса не нужно. Гейтить по нему обязательно: разделы, которые в agents не
// ходят (/projects читает properties), 403-ом из кабинета не закроешь.
const agentStatus = ref<string | undefined>(undefined)

const gateBlocked = (): void => {
  const target = blockedRedirect(agentStatus.value, route.name)
  if (target) router.replace(target)
}

onMounted(async () => {
  if (!isAuthenticated.value) return

  connectStream()

  try {
    const p = await agentsApi.getMyProfile()
    profileName.value = p.fullName ?? ''
    profileAgency.value = p.agency ?? ''
    profileReward.value = typeof p.rewardPercent === 'number' ? p.rewardPercent : null
    agentStatus.value = p.agentStatus
    gateBlocked()
  } catch {
    // карточка деградирует до пустой — не блокируем кабинет
  }
})

// Из поддержки блокировка не уводит (канал апелляции), но и уйти оттуда в
// закрытые разделы нельзя: layout смонтирован один раз, профиль уже прочитан.
watch(() => route.name, gateBlocked)

onUnmounted(() => {
  disconnectStream()
})

// Реконнект стрима при (раз)логине в рамках одной сессии SPA.
watch(isAuthenticated, (authed) => {
  if (authed) connectStream()
  else disconnectStream()
})

const initials = computed(() => {
  const parts = profileName.value.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return 'A'
  return parts
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('')
})

interface NavItem {
  key: string
  letter: string
  to: string
  enabled: boolean
  /** Ключ раздела в карте (DEV-1265); по нему пункт и скрывается. */
  section: SectionKey
}

// Разделы кабинета v2 (все живые).
//
// ⚠️ Это ВЁРСТКА меню, а не модель доступа. Разделов в реестре семнадцать,
// здесь девять: остальные открываются с дашборда и по прямым ссылкам. Строить
// меню прямо из карты нельзя — появление ключа на сервере молча добавило бы
// пункт в интерфейс, для которого нет ни буквы-иконки, ни перевода.
const allNavItems: NavItem[] = [
  { key: 'dashboard', letter: 'Д', to: '/dashboard', enabled: true, section: 'agent_dashboard' },
  { key: 'clients', letter: 'К', to: '/clients', enabled: true, section: 'agent_clients' },
  { key: 'deals', letter: 'С', to: '/deals', enabled: true, section: 'agent_deals' },
  { key: 'reward', letter: '₽', to: '/reward', enabled: true, section: 'agent_reward' },
  { key: 'education', letter: 'О', to: '/education', enabled: true, section: 'agent_education' },
  // DEV-1003: раздел «Проекты» — ЖК, доступные агенту.
  { key: 'projects', letter: 'П', to: '/projects', enabled: true, section: 'agent_projects' },
  // DEV-1015: раздел «Презентации» — обезличенные офферы по ЖК.
  {
    key: 'presentations',
    letter: 'П',
    to: '/presentations',
    enabled: true,
    section: 'agent_presentations',
  },
  { key: 'profile', letter: 'П', to: '/profile', enabled: true, section: 'agent_profile' },
  { key: 'support', letter: '?', to: '/support', enabled: true, section: 'agent_support' },
]

// Пункт исчезает, если раздела нет в карте принципала. В legacy-режиме
// hasSection пропускает всё, поэтому до выката бэкенда меню прежнее.
const navItems = computed(() =>
  allNavItems.filter((item) => sectionsStore.hasSection(item.section)),
)

const isActive = (to: string) => route.path === to || route.path.startsWith(`${to}/`)

const pageTitle = computed(() => {
  if (route.path.startsWith('/submit-client')) return t('agents.cabinet.submitClient')
  const active = navItems.value.find((i) => isActive(i.to))
  return active ? t(`agents.cabinet.nav.${active.key}`) : t('agents.cabinet.nav.dashboard')
})

function go(item: NavItem) {
  if (item.enabled) router.push(item.to)
}

// --- Мобильная навигация (DEV-498) ---
// Нижний таб-бар: 4 ключевых раздела + «Ещё» (открывает drawer).
const tabKeys = ['dashboard', 'clients', 'deals', 'reward']
const tabItems = computed(() => navItems.value.filter((i) => tabKeys.includes(i.key)))
// Разделы в drawer-меню. Координатор (Figma «Режим координатора») отложен — нужен
// company-wide бэкенд + роль (DEV-498/CO1, Итерация 4), поэтому здесь не выводим.
const menuKeys = ['profile', 'projects', 'presentations', 'education', 'support']
const menuItems = computed(() => navItems.value.filter((i) => menuKeys.includes(i.key)))

const mobileMenuOpen = ref(false)
const toggleMenu = () => (mobileMenuOpen.value = !mobileMenuOpen.value)
const closeMenu = () => (mobileMenuOpen.value = false)
// «Ещё» подсвечен, когда открыт текущий раздел из меню или передача клиента.
const moreActive = computed(
  () =>
    mobileMenuOpen.value ||
    route.path.startsWith('/submit-client') ||
    menuItems.value.some((i) => isActive(i.to)),
)

function goPath(to: string) {
  closeMenu()
  router.push(to)
}

// Закрываем меню при смене маршрута.
watch(() => route.path, closeMenu)

const localeOptions = availableLocales.map((l) => ({ value: l.code, label: `${l.flag} ${l.name}` }))
const currentLocale = computed<Locale>({
  get: () => locale.value as Locale,
  set: (value) => {
    locale.value = value
    setStoredLocale(value)
  },
})

async function onLogout() {
  if (impersonating) {
    await authStore.endImpersonation()
    return
  }
  await authStore.logout()
  router.replace('/login')
}

// На страницах без авторизации (login/register/offer) сайдбар не показываем.
const showChrome = computed(() => isAuthenticated.value && route.meta.requiresAuth !== false)

// Оферта (DEV-365) — длинный юридический документ, а не форма: в auth-сплите ему
// нужна широкая колонка с минимальным отступом (в отличие от узких форм входа/регистрации).
const isOfferPage = computed(() => route.name === 'agent-offer')
</script>

<template>
  <div class="agent-layout" :class="{ 'agent-layout--bare': !showChrome }">
    <!-- Публичный режим (вход/регистрация/оферта): продающий сплит-экран (Figma AD1–AD6) -->
    <template v-if="!showChrome">
      <div class="agent-auth">
        <AgentAuthPanel class="agent-auth__panel" />
        <section class="agent-auth__form" :class="{ 'agent-auth__form--doc': isOfferPage }">
          <div class="agent-auth__locale">
            <AppSelect v-model="currentLocale" :options="localeOptions" size="small" />
          </div>
          <div class="agent-auth__content" :class="{ 'agent-auth__content--doc': isOfferPage }">
            <router-view v-slot="{ Component }"><component :is="Component" /></router-view>
          </div>
        </section>
      </div>
    </template>

    <!-- Кабинет v2 -->
    <template v-else>
      <!-- Десктоп: сайдбар -->
      <aside class="agent-sidebar">
        <div class="agent-sidebar__brand" @click="router.push('/dashboard')">
          <img :src="logoBlue" class="agent-sidebar__logo" alt="VINPROP Agents" />
          <span class="agent-layout__sub">{{ t('agents.portalTitle') }}</span>
        </div>

        <button class="agent-sidebar__cta" type="button" @click="router.push('/submit-client')">
          + {{ t('agents.cabinet.submitClient') }}
        </button>

        <nav class="agent-sidebar__nav">
          <button
            v-for="item in navItems"
            :key="item.key"
            type="button"
            class="agent-nav-item"
            :class="{
              'agent-nav-item--active': isActive(item.to),
              'agent-nav-item--disabled': !item.enabled,
            }"
            :disabled="!item.enabled"
            @click="go(item)"
          >
            <span class="agent-nav-item__badge">{{ item.letter }}</span>
            <span class="agent-nav-item__label">{{ t(`agents.cabinet.nav.${item.key}`) }}</span>
            <span v-if="!item.enabled" class="agent-nav-item__soon">{{
              t('agents.cabinet.soon')
            }}</span>
          </button>
        </nav>

        <div class="agent-sidebar__spacer" />

        <div class="agent-sidebar__user">
          <span class="agent-sidebar__avatar">{{ initials }}</span>
          <div class="agent-sidebar__userinfo">
            <span class="agent-sidebar__username">{{ profileName || '—' }}</span>
            <span class="agent-sidebar__usercompany">{{ profileAgency }}</span>
          </div>
          <AppButton variant="tertiary" size="small" @click="onLogout">{{
            impersonating ? t('impersonation.endMenu') : t('agents.logout')
          }}</AppButton>
        </div>
      </aside>

      <div class="agent-main">
        <!-- Десктоп: топбар -->
        <header class="agent-topbar">
          <h1 class="agent-topbar__title">{{ pageTitle }}</h1>
          <div class="agent-topbar__actions">
            <button
              class="agent-bell"
              type="button"
              :aria-label="t('notifications.title')"
              @click="togglePanel"
            >
              <BellIcon :size="20" />
              <span v-if="unreadCount > 0" class="agent-bell__badge">
                {{ unreadCount > 99 ? '99+' : unreadCount }}
              </span>
            </button>
            <AppSelect v-model="currentLocale" :options="localeOptions" size="small" />
          </div>
        </header>

        <!-- Мобайл: верхний бар -->
        <header class="agent-mtop">
          <button
            class="agent-mtop__menu"
            type="button"
            :aria-label="t('agents.cabinet.more')"
            @click="toggleMenu"
          >
            ☰
          </button>
          <span class="agent-mtop__title">{{ pageTitle }}</span>
          <button
            class="agent-bell agent-bell--mobile"
            type="button"
            :aria-label="t('notifications.title')"
            @click="togglePanel"
          >
            <BellIcon :size="20" />
            <span v-if="unreadCount > 0" class="agent-bell__badge">
              {{ unreadCount > 99 ? '99+' : unreadCount }}
            </span>
          </button>
          <span class="agent-mtop__avatar">{{ initials }}</span>
        </header>

        <main class="agent-main__content">
          <router-view v-slot="{ Component }"><component :is="Component" /></router-view>
        </main>

        <!-- Мобайл: нижний таб-бар -->
        <nav class="agent-mtabs">
          <button
            v-for="item in tabItems"
            :key="item.key"
            type="button"
            class="agent-mtab"
            :class="{ 'agent-mtab--active': isActive(item.to) }"
            @click="go(item)"
          >
            <span class="agent-mtab__badge">{{ item.letter }}</span>
            <span class="agent-mtab__label">{{ t(`agents.cabinet.nav.${item.key}`) }}</span>
          </button>
          <button
            type="button"
            class="agent-mtab"
            :class="{ 'agent-mtab--active': moreActive }"
            @click="toggleMenu"
          >
            <span class="agent-mtab__badge">•</span>
            <span class="agent-mtab__label">{{ t('agents.cabinet.more') }}</span>
          </button>
        </nav>
      </div>

      <!-- Мобайл: drawer-меню «Ещё» -->
      <div v-if="mobileMenuOpen" class="agent-mmenu">
        <div class="agent-mmenu__overlay" @click="closeMenu" />
        <div class="agent-mmenu__panel" role="dialog" aria-modal="true">
          <header class="agent-mmenu__head">
            <span class="agent-mmenu__title">{{ t('agents.cabinet.more') }}</span>
            <button class="agent-mmenu__close" type="button" aria-label="close" @click="closeMenu">
              ✕
            </button>
          </header>

          <!-- Юзер-карточка -->
          <div class="agent-mmenu__user">
            <span class="agent-mmenu__avatar">{{ initials }}</span>
            <div class="agent-mmenu__userinfo">
              <span class="agent-mmenu__username">{{ profileName || '—' }}</span>
              <span class="agent-mmenu__usercompany">{{ profileAgency }}</span>
            </div>
            <span v-if="profileReward !== null" class="agent-mmenu__reward"
              >{{ profileReward }}%</span
            >
          </div>

          <!-- CTA «Передать клиента» (на десктопе живёт в сайдбаре) -->
          <button class="agent-mmenu__cta" type="button" @click="goPath('/submit-client')">
            + {{ t('agents.cabinet.submitClient') }}
          </button>

          <!-- Разделы -->
          <div class="agent-mmenu__group">
            <p class="agent-mmenu__group-title">{{ t('agents.cabinet.menu.sections') }}</p>
            <button
              v-for="item in menuItems"
              :key="item.key"
              type="button"
              class="agent-mmenu__row"
              @click="goPath(item.to)"
            >
              <span class="agent-mmenu__row-badge" :data-key="item.key">{{ item.letter }}</span>
              <span class="agent-mmenu__row-text">
                <span class="agent-mmenu__row-title">{{
                  t(`agents.cabinet.nav.${item.key}`)
                }}</span>
                <span class="agent-mmenu__row-sub">{{
                  t(`agents.cabinet.menu.${item.key}Sub`)
                }}</span>
              </span>
              <span class="agent-mmenu__row-chevron">›</span>
            </button>
          </div>

          <!-- Настройки -->
          <div class="agent-mmenu__group">
            <p class="agent-mmenu__group-title">{{ t('agents.cabinet.menu.settings') }}</p>
            <div class="agent-mmenu__row agent-mmenu__row--static">
              <span class="agent-mmenu__row-badge" data-key="lang">RU</span>
              <span class="agent-mmenu__row-text">
                <span class="agent-mmenu__row-title">{{ t('agents.cabinet.menu.language') }}</span>
              </span>
              <AppSelect v-model="currentLocale" :options="localeOptions" size="small" />
            </div>
            <button type="button" class="agent-mmenu__row" @click="onLogout">
              <span class="agent-mmenu__row-badge" data-key="logout">⎋</span>
              <span class="agent-mmenu__row-text">
                <span class="agent-mmenu__row-title">{{
                  impersonating ? t('impersonation.endMenu') : t('agents.logout')
                }}</span>
              </span>
              <span class="agent-mmenu__row-chevron">›</span>
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- Панель уведомлений (Teleport to body; DEV-1017). -->
    <NotificationPanel />
  </div>
</template>

<style scoped>
.agent-layout {
  /* Пин светлой палитры дизайн-системы для всего поддерева (см. DEV-279/372):
     base.css флипает переменные по prefers-color-scheme: dark, что ломает контраст. */
  color-scheme: light;
  --color-background: #ffffff;
  --color-background-soft: #fafafa;
  --color-background-mute: #f4f4f5;
  --color-surface: #ffffff;
  --color-text: #18181b;
  --color-text-primary: #18181b;
  --color-text-secondary: #52525b;
  --color-text-tertiary: #71717a;
  --color-heading: #18181b;
  --color-border: #ecedf1;
  --cab-primary: #4f46e5;
  --cab-primary-soft: #eef2ff;
  --cab-bg: #f6f7f9;
  --cab-muted: #a1a1aa;

  /* Плашка вкладки просмотра (DEV-2400) стоит над каркасом — вычитаем её. */
  min-height: calc(100vh - var(--impersonation-banner-height, 0px));
  background: var(--cab-bg);
  color: var(--color-text-primary);
}

.agent-layout:not(.agent-layout--bare) {
  display: flex;
  align-items: stretch;
}

.agent-sidebar__logo {
  height: 26px;
  width: auto;
  display: block;
}

.agent-layout__sub {
  font-size: 0.6875rem;
  color: var(--color-text-tertiary);
}

/* --- Публичный режим: продающий сплит-экран (Figma AD1–AD6) --- */
.agent-layout--bare {
  display: block;
}

.agent-auth {
  display: grid;
  grid-template-columns: 47fr 53fr;
  min-height: 100vh;
}

.agent-auth__form {
  position: relative;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 40px;
}

.agent-auth__locale {
  position: absolute;
  top: 20px;
  right: 24px;
}

.agent-auth__content {
  width: 100%;
  max-width: 452px;
}

/* Оферта: широкая колонка документа с минимальным отступом (не узкая форма). */
.agent-auth__form--doc {
  align-items: flex-start;
  padding: 44px 20px 40px;
}

.agent-auth__content--doc {
  max-width: 100%;
}

@media (max-width: 900px) {
  .agent-auth {
    grid-template-columns: 1fr;
  }

  .agent-auth__form {
    padding: 32px 24px 44px;
  }
}

/* --- Сайдбар (десктоп) --- */
.agent-sidebar {
  width: 240px;
  min-height: calc(100vh - var(--impersonation-banner-height, 0px));
  background: #fff;
  border-right: 1px solid var(--color-border);
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px;
  position: sticky;
  top: var(--impersonation-banner-height, 0px);
}

.agent-sidebar__brand {
  display: flex;
  flex-direction: column;
  align-items: flex-start; /* иначе flex-column растягивает <img> лого по ширине (искажение) */
  cursor: pointer;
  line-height: 1.1;
  padding: 0 4px 8px;
}

.agent-sidebar__cta {
  height: 40px;
  border: none;
  border-radius: 10px;
  background: var(--cab-primary);
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 8px;
}

.agent-sidebar__cta:hover {
  background: #4338ca;
}

.agent-sidebar__nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.agent-nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 40px;
  padding: 0 10px;
  border: none;
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  width: 100%;
}

.agent-nav-item:hover:not(.agent-nav-item--disabled) {
  background: var(--color-background-mute);
}

.agent-nav-item--active {
  background: var(--cab-primary-soft);
}

.agent-nav-item--disabled {
  cursor: default;
}

.agent-nav-item__badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 7px;
  background: var(--color-background-mute);
  color: var(--color-text-tertiary);
  font-size: 0.75rem;
  font-weight: 700;
  flex-shrink: 0;
}

.agent-nav-item--active .agent-nav-item__badge {
  background: var(--cab-primary);
  color: #fff;
}

.agent-nav-item__label {
  flex: 1;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text-secondary);
}

.agent-nav-item--active .agent-nav-item__label {
  color: #4338ca;
  font-weight: 600;
}

.agent-nav-item--disabled .agent-nav-item__label {
  color: var(--cab-muted);
}

.agent-nav-item__soon {
  font-size: 0.625rem;
  color: var(--cab-muted);
  border: 1px solid var(--color-border);
  border-radius: 999px;
  padding: 1px 6px;
}

.agent-sidebar__spacer {
  flex: 1;
}

.agent-sidebar__user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  border-radius: 10px;
  background: var(--color-background-soft);
}

.agent-sidebar__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  background: var(--cab-primary-soft);
  color: var(--cab-primary);
  font-size: 0.75rem;
  font-weight: 700;
  flex-shrink: 0;
}

.agent-sidebar__userinfo {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.agent-sidebar__username {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--color-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.agent-sidebar__usercompany {
  font-size: 0.6875rem;
  color: var(--cab-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* --- Main --- */
.agent-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.agent-topbar {
  height: 64px;
  background: #fff;
  border-bottom: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
}

.agent-topbar__title {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 0;
}

.agent-topbar__actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Колокольчик уведомлений (DEV-1017). */
.agent-bell {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  border-radius: 10px;
  color: var(--color-text, #18181b);
  cursor: pointer;
  transition: background-color 0.15s;
}
.agent-bell:hover {
  background: var(--color-background-mute, #f4f4f5);
}
.agent-bell__badge {
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #e5484d;
  color: #fff;
  font-size: 0.625rem;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
}
.agent-bell--mobile {
  width: 36px;
  height: 36px;
}

.agent-main__content {
  flex: 1;
  padding: 32px;
  background: var(--cab-bg);
}

/* --- Мобайл: верхний бар + нижний таб-бар + drawer (по умолчанию скрыты) --- */
.agent-mtop,
.agent-mtabs {
  display: none;
}

.agent-mtop {
  position: sticky;
  top: var(--impersonation-banner-height, 0px);
  z-index: 20;
  align-items: center;
  gap: 12px;
  height: 56px;
  padding: 0 16px;
  background: #fff;
  border-bottom: 1px solid var(--color-border);
}

.agent-mtop__menu {
  border: none;
  background: transparent;
  font-size: 1.125rem;
  color: var(--color-text-secondary);
  cursor: pointer;
  line-height: 1;
}

.agent-mtop__title {
  flex: 1;
  font-size: 1.0625rem;
  font-weight: 700;
  color: var(--color-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.agent-mtop__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  background: var(--cab-primary-soft);
  color: var(--cab-primary);
  font-size: 0.6875rem;
  font-weight: 700;
  flex-shrink: 0;
}

.agent-mtabs {
  position: sticky;
  bottom: 0;
  z-index: 20;
  align-items: center;
  height: 64px;
  padding: 0 8px;
  background: #fff;
  border-top: 1px solid var(--color-border);
}

.agent-mtab {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  border: none;
  background: transparent;
  cursor: pointer;
}

.agent-mtab__badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--cab-muted);
}

.agent-mtab__label {
  font-size: 0.625rem;
  font-weight: 500;
  color: var(--cab-muted);
}

.agent-mtab--active .agent-mtab__badge {
  background: var(--cab-primary-soft);
  color: var(--cab-primary);
}

.agent-mtab--active .agent-mtab__label {
  color: #4338ca;
  font-weight: 600;
}

/* drawer «Ещё» */
.agent-mmenu {
  position: fixed;
  inset: 0;
  z-index: 50;
}

.agent-mmenu__overlay {
  position: absolute;
  inset: 0;
  background: rgba(9, 9, 11, 0.5);
}

.agent-mmenu__panel {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  overflow-y: auto;
  background: var(--cab-bg);
}

.agent-mmenu__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.agent-mmenu__title {
  font-size: 1.0625rem;
  font-weight: 700;
  color: var(--color-text);
}

.agent-mmenu__close {
  border: none;
  background: transparent;
  font-size: 1.125rem;
  color: var(--cab-muted);
  cursor: pointer;
  line-height: 1;
}

.agent-mmenu__user {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background: #fff;
  border: 1px solid var(--color-border);
  border-radius: 12px;
}

.agent-mmenu__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 999px;
  background: var(--cab-primary-soft);
  color: var(--cab-primary);
  font-size: 0.9375rem;
  font-weight: 700;
  flex-shrink: 0;
}

.agent-mmenu__userinfo {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.agent-mmenu__username {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--color-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.agent-mmenu__usercompany {
  font-size: 0.75rem;
  color: var(--color-text-tertiary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.agent-mmenu__reward {
  display: flex;
  align-items: center;
  height: 23px;
  padding: 0 10px;
  border-radius: 999px;
  background: var(--cab-primary-soft);
  color: var(--cab-primary);
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
}

.agent-mmenu__cta {
  height: 44px;
  border: none;
  border-radius: 12px;
  background: var(--cab-primary);
  color: #fff;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
}

.agent-mmenu__group {
  background: #fff;
  border: 1px solid var(--color-border);
  border-radius: 12px;
  overflow: hidden;
}

.agent-mmenu__group-title {
  margin: 0;
  padding: 12px 16px;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text);
  border-bottom: 1px solid var(--color-border);
}

.agent-mmenu__row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 14px 16px;
  border: none;
  border-top: 1px solid var(--color-border);
  background: transparent;
  cursor: pointer;
  text-align: left;
}

.agent-mmenu__group-title + .agent-mmenu__row,
.agent-mmenu__group .agent-mmenu__row:first-of-type {
  border-top: none;
}

.agent-mmenu__row--static {
  cursor: default;
}

.agent-mmenu__row-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: var(--color-background-mute);
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  font-weight: 700;
  flex-shrink: 0;
}

.agent-mmenu__row-badge[data-key='profile'] {
  background: var(--cab-primary-soft);
  color: var(--cab-primary);
}

.agent-mmenu__row-badge[data-key='education'] {
  background: #fffbeb;
  color: #b45309;
}

.agent-mmenu__row-badge[data-key='logout'] {
  background: #fef2f2;
  color: #be123c;
}

.agent-mmenu__row-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.agent-mmenu__row-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text);
}

.agent-mmenu__row-sub {
  font-size: 0.75rem;
  color: var(--cab-muted);
}

.agent-mmenu__row-chevron {
  color: var(--cab-muted);
  font-size: 1.125rem;
  flex-shrink: 0;
}

@media (max-width: 900px) {
  /* Прячем десктоп-хром, показываем мобильный */
  .agent-sidebar,
  .agent-topbar {
    display: none;
  }

  .agent-mtop,
  .agent-mtabs {
    display: flex;
  }

  .agent-main__content {
    padding: 16px;
  }
}

@media (min-width: 901px) {
  /* На десктопе drawer не показываем, даже если остался открыт после ресайза */
  .agent-mmenu {
    display: none;
  }
}
</style>
